import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";

const HONOR_CAMPER_QUALITIES = [
  "Pep",
  "Perseverance",
  "Clean Thoughts / Clean Speech",
  "Honesty",
  "Good Manners",
  "Cleanliness",
  "Dependable",
  "Camp Spirit",
  "Sportsmanship",
  "Responsibility",
  "Generosity",
  "Initiative",
  "Self-control",
  "Consideration",
  "Willingness / Helpfulness",
  "Leadership",
  "Good Fellowship",
];

const schema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type FormData = z.infer<typeof schema>;

export default function Login() {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [qualityIndex, setQualityIndex] = useState(
    () => Math.floor(Math.random() * HONOR_CAMPER_QUALITIES.length),
  );

  useEffect(() => {
    const id = setInterval(() => {
      setQualityIndex((prev) => (prev + 1) % HONOR_CAMPER_QUALITIES.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left — photo panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Placeholder photo block — replace with real camp photo */}
        <div className="photo-placeholder w-full h-full">
          <div className="relative z-10 flex flex-col justify-between p-12 h-full">
            <img src="/ch-logo-white.png" alt="Camp Highlands" className="h-12 w-auto" />
            <div className="text-white">
              <p className="text-2xl font-display font-bold leading-snug">
                Camp Highlands for Boys since 1904
              </p>
              <p className="mt-3 text-forest-200 text-lg italic transition-opacity duration-700">
                {HONOR_CAMPER_QUALITIES[qualityIndex]}
              </p>
            </div>
          </div>
        </div>

        {/* Forest texture overlay grid */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,transparent,transparent 40px,rgba(255,255,255,.1) 40px,rgba(255,255,255,.1) 41px),repeating-linear-gradient(90deg,transparent,transparent 40px,rgba(255,255,255,.1) 40px,rgba(255,255,255,.1) 41px)",
          }}
        />
      </div>

      {/* Right — login form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16 bg-white">
        <div className="max-w-sm w-full mx-auto">
          {/* Logo */}
          <img src="/ch-logo-green.png" alt="Camp Highlands" className="h-14 w-auto mb-10" />

          <h1 className="text-2xl font-display font-bold text-stone-900 mb-1">
            Welcome back
          </h1>
          <p className="text-stone-500 text-sm mb-8">
            Sign in to access your camp&apos;s ACTS dashboard.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              placeholder="you@yourcamp.com"
              error={errors.email?.message}
              {...register("email")}
            />

            <div>
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                error={errors.password?.message}
                {...register("password")}
              />
              <div className="text-right mt-1">
                <Link
                  to="/forgot-password"
                  className="text-sm text-forest-600 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              className="w-full mt-2"
            >
              Sign in
            </Button>
          </form>

          <p className="mt-8 text-xs text-stone-400 text-center">
            Need access? Contact your camp director.
          </p>

          <img src="/ch-logo-green.png" alt="Camp Highlands" className="h-6 w-auto opacity-40 mx-auto mt-6" />
        </div>
      </div>
    </div>
  );
}
